import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { buildTtqWorkbenchSourceGroupItems } from '../../features/ttqib/buildTtqWorkbenchSourceGroupItems';
import { TtqWorkbenchSourceParticleGroup } from '../../features/ttqib/TtqWorkbenchSourceParticle';
import { TtqWorkbenchFocusLayout } from '../../features/ttqib/TtqWorkbenchSplitLayout';
import { TtqWorkbenchToolbar, TtqWorkbenchToolbarAction } from '../../features/ttqib/TtqWorkbenchToolbar';
import { useTtqWorkbenchFocusEmphasis } from '../../features/ttqib/useTtqWorkbenchFocusEmphasis';
import { TtqWorkbenchShell } from '../../features/ttqib/ttqWorkbenchShared';
import { parseTtqWorkbenchParticles } from '../../features/ttqib/ttqWorkbenchRoutes';
import { ttqService, type TtqSession } from '../../services/ttqService';

export function TtqSessionAggregateBWorkbenchPage() {
  const { ttqaId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const particleIds = useMemo(() => parseTtqWorkbenchParticles(searchParams), [searchParams]);
  const [session, setSession] = useState<TtqSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void ttqService.getSession(ttqaId)
      .then((s) => active && setSession(s))
      .catch((e) => active && setError(e instanceof Error ? e.message : '加载失败'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [ttqaId]);

  const sourceEntries = useMemo(
    () => (session?.particles ?? []).filter((e) => particleIds.includes(e.particleId)),
    [particleIds, session?.particles],
  );
  const groupItems = useMemo(
    () => buildTtqWorkbenchSourceGroupItems(sourceEntries),
    [sourceEntries],
  );
  const canRun = Boolean(session?.correlationAnalysis);
  const hasResult = Boolean(session?.variantInterleaveSuggestion);
  const split = hasResult;
  const { emphasis, setEmphasis } = useTtqWorkbenchFocusEmphasis(split);

  const handleRun = async () => {
    if (!session) return;
    setRunning(true);
    setError(null);
    try {
      setSession(await ttqService.runSessionB(session.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : '分析失败');
    } finally {
      setRunning(false);
    }
  };

  return (
    <TtqWorkbenchShell
      ttqaId={ttqaId}
      title="变式穿插建议"
      angleId="session_aggregate_b"
      loading={loading}
      error={error}
      restoreParticleIds={particleIds}
      toolbar={(
        <TtqWorkbenchToolbar
          showFocusToggle={split}
          leftLabel="源题"
          rightLabel="建议"
          emphasis={emphasis}
          onEmphasisChange={setEmphasis}
        >
          <TtqWorkbenchToolbarAction
            disabled={running}
            loading={running}
            onClick={() => { void handleRun(); }}
          >
            生成变式穿插建议
          </TtqWorkbenchToolbarAction>
        </TtqWorkbenchToolbar>
      )}
    >
      {!canRun ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          尚未完成共同隐性能力挖掘。可先
          {' '}
          <button
            type="button"
            className="font-medium text-primary underline"
            onClick={() => navigate(`/ttq/${ttqaId}/ttqworkbench/aggregate-a?particles=${particleIds.map(encodeURIComponent).join(',')}`)}
          >
            前往运行
          </button>
          ，或直接点击 toolbar 按钮尝试生成（若后端已具备数据）。
        </p>
      ) : null}

      <p className="text-xs text-muted">来源颗粒（{sourceEntries.length} 颗）</p>
      <TtqWorkbenchFocusLayout
        split={split}
        emphasis={emphasis}
        source={<TtqWorkbenchSourceParticleGroup items={groupItems} />}
        derived={hasResult ? (
          <section className="rounded-xl border border-border bg-surface p-4 shadow-sm">
            <p className="text-xs font-medium text-muted">穿插建议</p>
            <p className="mt-2 text-sm text-ink">{session!.variantInterleaveSuggestion!.narrative}</p>
          </section>
        ) : null}
      />
      {!hasResult ? (
        <p className="text-sm text-muted">运行后将在此展示变式穿插练习建议。</p>
      ) : null}
    </TtqWorkbenchShell>
  );
}
